import * as React from "react";
import PropTypes from "prop-types";
import Table from "@material-ui/core/Table/Table";
import TableHead from "@material-ui/core/TableHead/TableHead";
import TableRow from "@material-ui/core/TableRow/TableRow";
import TableCell from "@material-ui/core/TableCell/TableCell";
import TableBody from "@material-ui/core/TableBody/TableBody";
import withStyles from "@material-ui/core/styles/withStyles";
import PercentageNumber from "../UI/PercentageNumber";
import { connect } from "react-redux";
import { sortPortfolioOutput, stableSort } from "../../utilities/sorting";
import { isPremiumView } from "../../utilities/access";
import {END_OF_CPR_YEAR} from "../../utilities/cpr";

const styles = (theme) => ({
  cyPortfolioTable: {
    marginBottom: 10,
    "@media print": {
      width: "100%",
    },
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  summaryRow: {
    background: theme.palette.grey[200],
    "-webkit-print-color-adjust": "exact", // PDF/Print styling
  },

  tableCell: {
    padding: 2,
    fontSize: 12,
    width: "10%",
    "&.header": {
      fontSize: 13,
      wordWrap: "break-word",
      fontWeight: 500,
      color: theme.palette.primary.main,
      maxWidth: 75,
      lineHeight: 1.5,
      "&.extra": {
        maxWidth: 90,
      },
      "&.extraLarge": {
        maxWidth: 150,
      },
      "&.firstColumn": {
        width: "30%",
      },
    },
    "&.summary-cell": {
      fontWeight: 500,
      padding: "0 2px",
      borderBottom: "none",
      height: 24,
      lineHeight: 2,
    },
    "&.wrap-cell": {
      maxWidth: 120,
      wordWrap: "break-word",
      "&.highlighted": {
        fontWeight: 500,
        maxWidth: 120,
      },
      "&.head": {
        maxWidth: 140,
      },
    },
  },
});

class CyPortfolioRiskMetrics extends React.Component {
  renderPerformanceTable = (manager, i) => {
    const { classes } = this.props;
    return (
      <TableRow key={i}>
        <TableCell className={`${classes.tableCell} wrap-cell head`}>
          {manager.managerDetail["ImplementationName"]}
        </TableCell>
        <TableCell className={`${classes.tableCell} wrap-cell head`}>
          {manager.managerDetail["GlobalCategory"]}
        </TableCell>
        <TableCell align="right" className={`${classes.tableCell} wrap-cell`}>
          <PercentageNumber value={ manager.managerDetail["AlphaBenchmark"]} />
        </TableCell>
        <TableCell align="right" className={`${classes.tableCell} wrap-cell`}>
          <PercentageNumber value={ manager.managerDetail["BetaBenchmark"]} needCalculation={false} showPercentageSign={false}/>
        </TableCell>
        <TableCell align="right" className={`${classes.tableCell} wrap-cell`}>
          <PercentageNumber
            value={manager.managerDetail["AnnualizedStandardDeviation"]}
          />
        </TableCell>
        <TableCell align="right" className={`${classes.tableCell} wrap-cell`}>
          <PercentageNumber value={ manager.managerDetail["AnnualizedSharpeRatio"]}  needCalculation={false} showPercentageSign={false}/>
        </TableCell>
        <TableCell align="right" className={`${classes.tableCell} wrap-cell`}>
          <PercentageNumber value={ manager.managerDetail["SortinoRatio"]}  needCalculation={false} showPercentageSign={false}/>
        </TableCell>
      </TableRow>
    );
  };

  render() {
    const {
      classes,
      managerList,
      portfolioSummary,
      useWbiSingleStrategy,
      sortedPortfolioOrders,
      forPrinting,
      portfolioDisplayName,
      bdIsCambridge,
      accessLevel,
    } = this.props;
    const dateValue = END_OF_CPR_YEAR
      .toDate()
      .toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    const { orderBy, order } = sortedPortfolioOrders;
    const hiddenCell = (bdIsCambridge || !isPremiumView(accessLevel)) && forPrinting;

    return (
      <React.Fragment>
        {
          <Table className={classes.cyPortfolioTable}>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${classes.tableCell} header firstColumn`}
                >
                  {forPrinting
                    ? "Fund or Investment Strategy"
                    : `For the Date Range Ending ${dateValue}`}
                </TableCell>
                <TableCell className={`${classes.tableCell} header extra`}>
                  Asset Category
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} header`}
                >
                  Alpha
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} header`}
                >
                  Beta
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} header`}
                >
                  Standard
                  <br />
                  Deviation
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} header`}
                >
                  Sharpe Ratio
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} header`}
                >
                  Sortino Ratio
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow className={classes.summaryRow}>
                <TableCell
                  className={`${classes.tableCell} wrap-cell highlighted`}
                  colSpan={2}
                >
                  {useWbiSingleStrategy
                    ? "Selected Portfolio"
                    : `Cy Optimized Portfolio `}{" "}
                  ({portfolioDisplayName})
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} wrap-cell highlighted`}
                >
                  {
                    !hiddenCell &&
                    <PercentageNumber
                      value={
                        portfolioSummary.recommendationSummary["AlphaBenchmark"]
                      }
                    />
                  }
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} wrap-cell highlighted`}
                >
                  {
                    !hiddenCell &&
                    <PercentageNumber
                      value={
                        portfolioSummary.recommendationSummary[
                          "BetaBenchmark"
                          ]
                      }
                      needCalculation={false}
                      showPercentageSign={false}
                    />
                  }
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} wrap-cell highlighted`}
                >
                  {
                    !hiddenCell &&
                    <PercentageNumber
                      value={
                        portfolioSummary.recommendationSummary[
                          "AnnualizedStandardDeviation"
                          ]
                      }
                    />
                  }
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} wrap-cell highlighted`}
                >
                  {
                    !hiddenCell &&
                    <PercentageNumber
                      value={
                        portfolioSummary.recommendationSummary[
                          "AnnualizedSharpeRatio"
                          ]
                      }
                      needCalculation={false}
                      showPercentageSign={false}
                    />
                  }
                </TableCell>
                <TableCell
                  align="right"
                  className={`${classes.tableCell} wrap-cell highlighted`}
                >
                  {
                    !hiddenCell &&
                    <PercentageNumber
                      value={
                        portfolioSummary.recommendationSummary[
                          "SortinoRatio"
                          ]
                      }
                      needCalculation={false}
                      showPercentageSign={false}
                    />
                  }
                </TableCell>
              </TableRow>
              {stableSort(managerList, sortPortfolioOutput(order, orderBy))
                .filter((manager) => manager.managerCode !== "USD Cash")
                .map((manager, index) =>
                  this.renderPerformanceTable(manager, index)
                )}
              {managerList
                .filter((manager) => manager.managerCode === "USD Cash")
                .map((manager, index) =>
                  this.renderPerformanceTable(manager, index)
                )}
            </TableBody>
          </Table>
        }
      </React.Fragment>
    );
  }
}

CyPortfolioRiskMetrics.propTypes = {
  classes: PropTypes.object.isRequired,
  managerList: PropTypes.arrayOf(
    PropTypes.shape({
      managerDetail: PropTypes.shape({
        Name: PropTypes.string.isRequired,
        GlobalCategory: PropTypes.string.isRequired,
        AlphaBenchmark: PropTypes.string.isRequired,
        BetaBenchmark: PropTypes.string.isRequired,
        AnnualizedStandardDeviation: PropTypes.string.isRequired,
        AnnualizedSharpeRatio: PropTypes.string.isRequired,
        ImplementationName: PropTypes.string.isRequired,
        SortinoRatio: PropTypes.string.isRequired,
      }),
    })
  ).isRequired,
  portfolioDisplayName: PropTypes.string,
  portfolioSummary: PropTypes.shape({
    recommendationSummary: PropTypes.object,
    benchmark: PropTypes.object,
  }).isRequired,
  startDate: PropTypes.string,
  forPrinting: PropTypes.bool,
  bdIsCambridge: PropTypes.bool,
  accessLevel: PropTypes.bool,
  useWbiSingleStrategy: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    sortedPortfolioOrders: state.portfolio.sortedPortfolioOrders,
    appName: state.app.appName,
  };
};

export default withStyles(styles)(
  connect(mapStateToProps)(CyPortfolioRiskMetrics)
);



// WEBPACK FOOTER //
// ./src/components/Charts/CyPortfolioRiskMetrics.jsx